/**
 * PKCE / state / nonce utilities. Random values use Web Crypto; code_challenge uses
 * SHA-256. All outputs are base64url-encoded.
 */

const getCrypto = () => {
  const cryptoObj = (typeof window !== 'undefined' && (window.crypto || window.msCrypto)) || null;
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new Error('Web Crypto API is not available; cannot generate PKCE parameters');
  }
  return cryptoObj;
};

/** Encode an ArrayBuffer / Uint8Array as base64url (no padding). */
export const toBase64Url = (input) => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

/** Generate a random base64url string of the given byte size. */
export const createRandomString = (size = 64) => {
  const bytes = new Uint8Array(size);
  getCrypto().getRandomValues(bytes);
  return toBase64Url(bytes);
};

/** PKCE code_verifier (96 bytes -> ~128 base64url chars). */
export const createCodeVerifier = () => createRandomString(96);

/** Derive the PKCE code_challenge: base64url(SHA-256(verifier)). */
export const createCodeChallenge = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await getCrypto().subtle.digest('SHA-256', data);
  return toBase64Url(digest);
};

/** CSRF state parameter. */
export const createState = () => createRandomString(32);

/** OIDC nonce binding the id_token to this authorization request. */
export const createNonce = () => createRandomString(32);
